import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import * as alertifyjs from 'alertifyjs';
import { AuthService } from 'src/app/services/auth/auth.service';
import {
    CarouselConfigService,
    CarouselItem,
    CarouselItemFields,
    CarouselKind,
    CarouselLimits,
} from 'src/app/services/app-config/carousel-config.service';
import { checkMarketingImage, MarketingImageInfo, MARKETING_IMAGE_ACCEPT } from '../marketing-image.validator';
import {
    AppAction,
    AppActionType,
    APP_INTERNAL_ROUTES,
    describeAppTap,
    findAppInternalRoute,
    toAppActionType,
    validateAppAction,
} from '../app-action';

/** Same permission that gates the other switches of the parent screen. */
const EDIT_PERMISSION = 'config.app_movil.config';

/** Used until the backend answers; the live values travel in `limits` of the GET. */
const DEFAULT_LIMITS: CarouselLimits = {
    max_active_per_kind: 5,
    image: {
        max_bytes: 2 * 1024 * 1024,
        min_width: 1024,
        ratio: '16:9',
        ratio_tolerance: 0.05,
        recommended_width: 1600,
        recommended_max_bytes: 500 * 1024,
        formats: ['png', 'jpg', 'webp'],
    },
    icons: ['shield', 'trophy', 'crown', 'star', 'sparkles', 'lightbulb', 'megaphone'],
    text: { title: 30, title_with_cta: 24, description: 80, cta_label: 12, alt_text: 120, image_name: 60 },
};

/** What each of the seven app icons means, for the picker. */
const ICON_LABELS: Record<string, string> = {
    shield: 'Escudo — protección, seguros',
    trophy: 'Trofeo — logros, premios',
    crown: 'Corona — nivel, beneficios',
    star: 'Estrella — destacado',
    sparkles: 'Destellos — novedad, lanzamiento',
    lightbulb: 'Bombillo — tip, recomendación',
    megaphone: 'Megáfono — anuncio, promoción',
};

/** Font Awesome class that stands in for each app icon in the list and the preview. */
const ICON_CLASSES: Record<string, string> = {
    shield: 'fa-shield-alt',
    trophy: 'fa-trophy',
    crown: 'fa-crown',
    star: 'fa-star',
    sparkles: 'fa-magic',
    lightbulb: 'fa-lightbulb',
    megaphone: 'fa-bullhorn',
};

/**
 * Width of a strip item in the 206 px phone mock, from the app's slot math on a 390 dp phone
 * (`BannerSlotMetrics.resolve`: content 350 dp, margin 20 dp, gap 10 dp, target 320 / 360 dp):
 * one item fills the row; two or more overflow at (350 + 20 − 10) / 1.15 ≈ 313 dp, which the
 * 206 / 350 scale turns into 184 px.
 */
const PHONE_STRIP_ITEM_PX = { single: 206, overflow: 184 };

/** What the phone mock needs to paint one icon card, whether from the draft or from a live banner. */
export interface PhoneCard {
    icon: string;
    title: string;
    subtitle: string;
    /** Empty when the app would not paint the button (no action, or no label). */
    cta: string;
    titlePlaceholder: boolean;
    subtitlePlaceholder: boolean;
}

/** The customers live in Venezuela: validity dates are entered and shown in Caracas time. */
const CARACAS_TIME_ZONE = 'America/Caracas';
const CARACAS_OFFSET = '-04:00';

type EditorMode = 'create' | 'edit' | 'image';

/** The editor's working copy; compared field by field against nothing — the backend audits the diff. */
interface Draft {
    kind: CarouselKind;
    title: string;
    description: string;
    icon: string;
    ctaLabel: string;
    altText: string;
    actionType: AppActionType;
    internalRoute: string;
    externalUrl: string;
    /** `YYYY-MM-DD` in Caracas time, empty for no bound. */
    startsOn: string;
    endsOn: string;
    status: boolean;
}

const emptyDraft = (kind: CarouselKind): Draft => ({
    kind,
    title: '',
    description: '',
    icon: 'megaphone',
    ctaLabel: '',
    altText: '',
    actionType: '',
    internalRoute: '',
    externalUrl: '',
    startsOn: '',
    endsOn: '',
    status: true,
});

/** `YYYY-MM-DD` of an ISO instant as seen from Caracas; empty when there is none. */
function toCaracasDay(iso: string | null | undefined): string {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: CARACAS_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(date);
}

/** Start or end of a Caracas day as an ISO string the backend stores as-is. */
function fromCaracasDay(day: string, endOfDay: boolean): string {
    if (!day) return '';
    return `${day}T${endOfDay ? '23:59:59' : '00:00:00'}${CARACAS_OFFSET}`;
}

function formatDay(day: string): string {
    const [year, month, dayOfMonth] = day.split('-');
    return `${dayOfMonth}/${month}/${year}`;
}

function parseRatio(label: string): number {
    const [width, height] = label.split(':').map(Number);
    return width > 0 && height > 0 ? width / height : 16 / 9;
}

/**
 * «Carrusel» tab of Parámetros del Sistema → App Config: the banners the Flutter app shows in
 * its Home (DK-53). Everything goes through kredit-api-MT `/api/app-config/carrusel/v2`; the
 * BFF serves the result to the app within its app-config cache (up to 5 minutes).
 *
 * Items the Ionic-era console wrote arrive flagged `legacy` and can only be deleted; the
 * app never showed them since the Ionic was cut off.
 */
@Component({
    selector: 'app-carousel-config',
    templateUrl: './carousel-config.component.html',
    styleUrls: ['./carousel-config.component.css', '../app-phone.css']
})
export class CarouselConfigComponent implements OnInit, OnDestroy {
    loading = false;
    enabled = false;
    toggleLoading = false;
    items: CarouselItem[] = [];
    activeByKind: Record<CarouselKind, number> = { image: 0, icon: 0 };
    limits: CarouselLimits = DEFAULT_LIMITS;
    /** Id of the item whose row action is in flight; the other rows wait. */
    busyId: string | null = null;
    reordering = false;

    // Editor (modal): create a banner, edit its copy / action / window, or replace its image
    editorOpen = false;
    editorMode: EditorMode = 'create';
    editing: CarouselItem | null = null;
    draft: Draft = emptyDraft('image');
    saving = false;

    // Editor — the file picked for an image banner
    selectedFile: File | null = null;
    selectedImage: MarketingImageInfo | null = null;
    selectedImageError = '';
    selectedImageWarnings: string[] = [];
    imageChecking = false;
    selectedPreview: SafeUrl | null = null;
    private selectedObjectUrl = '';

    readonly internalRoutes = APP_INTERNAL_ROUTES;
    readonly imageAccept = MARKETING_IMAGE_ACCEPT;

    constructor(
        private carouselService: CarouselConfigService,
        private cdr: ChangeDetectorRef,
        private sanitizer: DomSanitizer,
        public _auth: AuthService
    ) {}

    ngOnInit(): void {
        this.load();
    }

    ngOnDestroy(): void {
        this.clearSelectedFile();
    }

    // ── Read model ──────────────────────────────────────────────────────────────────────

    get canEdit(): boolean {
        return this._auth.hasPermission(EDIT_PERMISSION);
    }

    get icons(): string[] {
        return this.limits.icons;
    }

    get maxActivePerKind(): number {
        return this.limits.max_active_per_kind;
    }

    /** The app shows at most N banners per kind; a sixth one is silently dropped. */
    get capacityNotices(): string[] {
        const notices: string[] = [];
        if (this.activeByKind.image > this.maxActivePerKind) {
            notices.push(`Hay ${this.activeByKind.image} banners de imagen activos y la app muestra como máximo ${this.maxActivePerKind}: los que sobran no se verán`);
        }
        if (this.activeByKind.icon > this.maxActivePerKind) {
            notices.push(`Hay ${this.activeByKind.icon} tarjetas con ícono activas y la app muestra como máximo ${this.maxActivePerKind}: las que sobran no se verán`);
        }
        return notices;
    }

    iconLabel(icon: string): string {
        return ICON_LABELS[icon] || icon;
    }

    iconClass(icon: string | undefined): string {
        return ICON_CLASSES[icon || ''] || 'fa-bullhorn';
    }

    kindLabel(item: CarouselItem): string {
        if (item.legacy) return 'Formato anterior (app Ionic)';
        return item.kind === 'image' ? 'Imagen' : 'Tarjeta con ícono';
    }

    actionLabel(item: CarouselItem): string {
        if (item.legacy) return item.action ? `Acción: ${item.action}` : 'Sin acción';
        const type = toAppActionType(item.action_type);
        if (type === 'internal') {
            const route = findAppInternalRoute(item.action_target || '');
            return `Abre: ${route ? route.label : item.action_target}`;
        }
        if (type === 'external') return `Abre: ${item.action_target}`;
        return 'Sin acción';
    }

    windowLabel(item: CarouselItem): string {
        const from = toCaracasDay(item.starts_at);
        const to = toCaracasDay(item.expires_at);
        if (!from && !to) return 'Sin fecha límite';
        if (from && to) return `Del ${formatDay(from)} al ${formatDay(to)}`;
        if (from) return `Desde el ${formatDay(from)}`;
        return `Hasta el ${formatDay(to)}`;
    }

    isExpired(item: CarouselItem): boolean {
        return !!item.expires_at && new Date(item.expires_at).getTime() < Date.now();
    }

    isScheduled(item: CarouselItem): boolean {
        return !!item.starts_at && new Date(item.starts_at).getTime() > Date.now();
    }

    imageMeta(item: CarouselItem): string {
        const parts: string[] = [];
        if (item.image_width && item.image_height) parts.push(`${item.image_width} × ${item.image_height} px`);
        if (item.image_bytes) parts.push(`${Math.round(item.image_bytes / 1024)} KB`);
        return parts.join(' · ');
    }

    trackById(_index: number, item: CarouselItem): string {
        return item.id;
    }

    // ── Viewer: what the app shows right now ───────────────────────────────────────────────

    /** Image banners the customer sees: active, inside their window, in order, capped as the app caps them. */
    get liveImages(): CarouselItem[] {
        return this.liveBanners('image');
    }

    /** Icon cards the customer sees, same rules. */
    get liveIcons(): CarouselItem[] {
        return this.liveBanners('icon');
    }

    get viewerEmpty(): boolean {
        return !this.enabled || (this.liveImages.length === 0 && this.liveIcons.length === 0);
    }

    /** One line on what the customer gets, for the viewer's header. */
    get viewerStatus(): string {
        if (!this.enabled) return 'El carrusel está apagado: la app no muestra ningún banner';
        const images = this.liveImages.length;
        const icons = this.liveIcons.length;
        if (images === 0 && icons === 0) return 'No hay banners activos y vigentes: la app no muestra el carrusel';
        const parts: string[] = [];
        if (images > 0) parts.push(images === 1 ? '1 imagen' : `${images} imágenes`);
        if (icons > 0) parts.push(icons === 1 ? '1 tarjeta' : `${icons} tarjetas`);
        return `El cliente ve ${parts.join(' y ')}`;
    }

    /** What the mock cannot say by itself: scrolling, rows, what stays out. */
    get viewerHints(): string[] {
        if (!this.enabled) return [];
        const hints: string[] = [];
        const images = this.liveImages.length;
        const icons = this.liveIcons.length;
        if (images > 0 && icons > 0) hints.push('Las imágenes van en una fila y las tarjetas en otra, debajo');
        if (images > 1 || icons > 1) {
            hints.push('Con más de una por fila, el cliente desliza de lado y ve un borde de la siguiente; aquí puedes desplazar la fila');
        }
        const skipped = this.items.filter(
            item => !item.legacy && item.status && (this.isExpired(item) || this.isScheduled(item))
        ).length;
        if (skipped > 0) hints.push(`${skipped} activo${skipped === 1 ? '' : 's'} fuera de su vigencia no se muestra${skipped === 1 ? '' : 'n'}`);
        const max = this.maxActivePerKind;
        if (this.activeByKind.image > max || this.activeByKind.icon > max) {
            hints.push(`La app muestra como máximo ${max} de cada tipo, en el orden de la lista: los que sobran no se ven`);
        }
        return hints;
    }

    /** Width of each item in a strip of `count` items, in the mock's pixels. */
    stripItemWidth(count: number): number {
        return count > 1 ? PHONE_STRIP_ITEM_PX.overflow : PHONE_STRIP_ITEM_PX.single;
    }

    /** The mock's card for a live banner; the button only when the app would paint it. */
    cardOf(item: CarouselItem): PhoneCard {
        const hasAction = toAppActionType(item.action_type) !== '';
        return {
            icon: item.icon || 'megaphone',
            title: item.title || '',
            subtitle: item.description || '',
            cta: hasAction ? item.cta_label || '' : '',
            titlePlaceholder: false,
            subtitlePlaceholder: false,
        };
    }

    private liveBanners(kind: CarouselKind): CarouselItem[] {
        return this.items
            .filter(item => !item.legacy && item.kind === kind && item.status && !this.isExpired(item) && !this.isScheduled(item))
            .slice(0, this.maxActivePerKind);
    }

    // ── Loading and the global switch ─────────────────────────────────────────────────────

    load = async () => {
        try {
            this.loading = true;
            const resp = await this.carouselService.getCarousel();
            const data = resp?.data;
            this.enabled = !!data?.status;
            this.items = Array.isArray(data?.list) ? data.list : [];
            this.activeByKind = {
                image: data?.active_by_kind?.image ?? 0,
                icon: data?.active_by_kind?.icon ?? 0,
            };
            this.limits = data?.limits ?? DEFAULT_LIMITS;
        } catch (error) {
            console.error('Error loading carousel:', error);
            alertifyjs.error('No se pudo cargar el carrusel');
            this.items = [];
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    };

    onGlobalToggleChange = async (event: Event) => {
        const checkbox = event.target as HTMLInputElement;
        const newValue = checkbox.checked;
        if (this.toggleLoading) {
            checkbox.checked = !newValue;
            return;
        }

        try {
            this.toggleLoading = true;
            const resp = await this.carouselService.setGlobalStatus(newValue);
            if (resp?.ok) {
                this.enabled = newValue;
                alertifyjs.success(newValue ? 'Carrusel activado' : 'Carrusel desactivado');
            } else {
                checkbox.checked = !newValue;
                alertifyjs.error(resp?.message || 'Error al actualizar el carrusel');
            }
        } catch (error) {
            checkbox.checked = !newValue;
            this.fail(error, 'Error al actualizar el carrusel');
        } finally {
            this.toggleLoading = false;
            this.cdr.detectChanges();
        }
    };

    // ── Row actions ────────────────────────────────────────────────────────────────────────

    canMove(index: number, delta: number): boolean {
        const target = index + delta;
        return target >= 0 && target < this.items.length;
    }

    /** Sends the whole order: the backend refuses a partial list, so a stale screen cannot scramble it. */
    move = async (index: number, delta: number) => {
        if (!this.canEdit || this.reordering || this.busyId || !this.canMove(index, delta)) return;
        const ids = this.items.map(item => item.id);
        const [moved] = ids.splice(index, 1);
        ids.splice(index + delta, 0, moved);

        try {
            this.reordering = true;
            const resp = await this.carouselService.reorder(ids);
            if (resp?.ok && Array.isArray(resp.data?.list)) {
                this.items = resp.data.list;
            } else {
                alertifyjs.error(resp?.message || 'No se pudo reordenar el carrusel');
            }
        } catch (error) {
            this.fail(error, 'No se pudo reordenar el carrusel');
        } finally {
            this.reordering = false;
            this.cdr.detectChanges();
        }
    };

    onItemToggleChange = async (item: CarouselItem, event: Event) => {
        const checkbox = event.target as HTMLInputElement;
        const newValue = checkbox.checked;
        if (this.busyId || item.legacy) {
            checkbox.checked = !newValue;
            return;
        }

        try {
            this.busyId = item.id;
            const resp = await this.carouselService.setItemStatus(item.id, newValue);
            if (resp?.ok) {
                this.replaceItem(resp.data.item);
                this.showWarnings(resp.data.warnings);
            } else {
                checkbox.checked = !newValue;
                alertifyjs.error(resp?.message || 'No se pudo cambiar el estado del banner');
            }
        } catch (error) {
            checkbox.checked = !newValue;
            this.fail(error, 'No se pudo cambiar el estado del banner');
        } finally {
            this.busyId = null;
            this.cdr.detectChanges();
        }
    };

    remove = async (item: CarouselItem) => {
        if (!this.canEdit || this.busyId) return;
        const what = item.legacy
            ? `el ítem «${item.title}» del formato anterior`
            : `el banner «${item.title || item.id}»`;
        const alsoAsset = item.kind === 'image' ? ' La imagen también se borra de Cloudinary.' : '';
        if (!confirm(`¿Eliminar ${what}?${alsoAsset}`)) return;

        try {
            this.busyId = item.id;
            const resp = await this.carouselService.deleteItem(item.id);
            if (resp?.ok) {
                alertifyjs.success('Banner eliminado');
                await this.load();
            } else {
                alertifyjs.error(resp?.message || 'No se pudo eliminar el banner');
            }
        } catch (error) {
            this.fail(error, 'No se pudo eliminar el banner');
        } finally {
            this.busyId = null;
            this.cdr.detectChanges();
        }
    };

    // ── Editor ─────────────────────────────────────────────────────────────────────────────

    get editorTitle(): string {
        if (this.editorMode === 'image') return 'Reemplazar la imagen';
        if (this.editorMode === 'edit') return this.draft.kind === 'image' ? 'Editar banner de imagen' : 'Editar tarjeta con ícono';
        return this.draft.kind === 'image' ? 'Nuevo banner de imagen' : 'Nueva tarjeta con ícono';
    }

    get showsImagePicker(): boolean {
        return this.draft.kind === 'image' && this.editorMode !== 'edit';
    }

    get showsFields(): boolean {
        return this.editorMode !== 'image';
    }

    get titleMax(): number {
        if (this.draft.kind === 'image') return this.limits.text.image_name;
        return this.draft.ctaLabel.trim() ? this.limits.text.title_with_cta : this.limits.text.title;
    }

    get draftAction(): AppAction {
        switch (this.draft.actionType) {
            case 'internal':
                return { type: 'internal', target: this.draft.internalRoute };
            case 'external':
                return { type: 'external', target: this.draft.externalUrl.trim() };
            default:
                return { type: '', target: '' };
        }
    }

    get draftTapHint(): string {
        return describeAppTap(this.draftAction);
    }

    /** First problem of the draft, Spanish, ready to show; empty when it can be published. */
    get draftError(): string {
        const draft = this.draft;
        const text = this.limits.text;

        if (this.editorMode === 'image') {
            if (!this.selectedFile) return 'Elige la imagen nueva';
            return this.selectedImageError;
        }

        if (draft.kind === 'icon') {
            const title = draft.title.trim();
            if (!title) return 'Escribe el título de la tarjeta';
            if (title.length > this.titleMax) {
                return `El título admite hasta ${this.titleMax} caracteres${draft.ctaLabel.trim() ? ' cuando la tarjeta lleva botón' : ''}`;
            }
            const description = draft.description.trim();
            if (!description) return 'Escribe el subtítulo de la tarjeta';
            if (description.length > text.description) return `El subtítulo admite hasta ${text.description} caracteres`;
            if (!this.icons.includes(draft.icon)) return 'Elige un ícono';
            if (draft.ctaLabel.trim().length > text.cta_label) return `El texto del botón admite hasta ${text.cta_label} caracteres`;
            if (draft.actionType !== '' && !draft.ctaLabel.trim()) {
                return 'Una tarjeta con acción necesita el texto del botón: en la app solo el botón es tocable';
            }
        } else {
            if (draft.title.trim().length > text.image_name) return `El nombre interno admite hasta ${text.image_name} caracteres`;
            if (draft.altText.trim().length > text.alt_text) return `El texto alternativo admite hasta ${text.alt_text} caracteres`;
            if (this.editorMode === 'create') {
                if (!this.selectedFile) return 'Elige la imagen del banner';
                if (this.selectedImageError) return this.selectedImageError;
            }
        }

        const actionError = validateAppAction(this.draftAction, 'el banner');
        if (actionError) return actionError;

        if (draft.startsOn && draft.endsOn && draft.endsOn < draft.startsOn) {
            return 'La fecha de fin debe ser posterior a la de inicio';
        }
        return '';
    }

    get canSubmit(): boolean {
        return this.canEdit && !this.saving && !this.imageChecking && !this.draftError;
    }

    /** What the preview shows: the file just picked, else the image live in the app. */
    get previewImage(): SafeUrl | string | null {
        return this.selectedPreview || this.editing?.image_url || null;
    }

    /** The app paints the button only when the card has an action and a label (`_IconBannerItem.showCta`). */
    get previewShowsCta(): boolean {
        return this.draft.actionType !== '' && this.draft.ctaLabel.trim() !== '';
    }

    /** The mock's card for the draft, with placeholders while the fields are empty. */
    get draftCard(): PhoneCard {
        const title = this.draft.title.trim();
        const subtitle = this.draft.description.trim();
        return {
            icon: this.draft.icon,
            title: title || 'Título de la tarjeta',
            subtitle: subtitle || 'Subtítulo de la tarjeta',
            cta: this.previewShowsCta ? this.draft.ctaLabel.trim() : '',
            titlePlaceholder: !title,
            subtitlePlaceholder: !subtitle,
        };
    }

    /** What the customer can and cannot do with the banner as drafted, next to the phone preview. */
    get previewHints(): string[] {
        if (this.draft.kind === 'image') {
            return [
                'La app recorta la imagen al centro para llenar el espacio 16:9 y no le pone texto encima',
                this.draft.actionType !== '' ? 'Toda la imagen es tocable' : 'Solo se muestra: no abre nada al tocarla',
            ];
        }
        const label = this.draft.ctaLabel.trim();
        let tap: string;
        if (this.previewShowsCta) {
            tap = `Solo el botón «${label}» es tocable`;
        } else if (this.draft.actionType !== '') {
            tap = 'Sin texto de botón la app no pinta el botón y la acción no se puede tocar';
        } else if (label) {
            tap = `El botón «${label}» no se muestra: la app solo lo pinta cuando la tarjeta tiene una acción al tocar`;
        } else {
            tap = 'Sin acción: la tarjeta solo informa';
        }
        return [
            'El título cabe en una línea y el subtítulo en dos; lo que sobra se corta con puntos suspensivos',
            tap,
        ];
    }

    openCreate(kind: CarouselKind): void {
        if (!this.canEdit) return;
        this.editorMode = 'create';
        this.editing = null;
        this.draft = emptyDraft(kind);
        this.clearSelectedFile();
        this.editorOpen = true;
    }

    openEdit(item: CarouselItem): void {
        if (!this.canEdit || item.legacy) return;
        this.editorMode = 'edit';
        this.editing = item;
        this.draft = this.draftFrom(item);
        this.clearSelectedFile();
        this.editorOpen = true;
    }

    openReplaceImage(item: CarouselItem): void {
        if (!this.canEdit || item.legacy || item.kind !== 'image') return;
        this.editorMode = 'image';
        this.editing = item;
        this.draft = this.draftFrom(item);
        this.clearSelectedFile();
        this.editorOpen = true;
    }

    closeEditor(): void {
        if (this.saving) return;
        this.editorOpen = false;
        this.editing = null;
        this.clearSelectedFile();
    }

    /** Operator picked a file: validate it client-side before enabling the publish button. */
    onFileSelected = async (event: Event) => {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] ?? null;
        // The input is emptied at once: the draft owns the file, and picking the same file again still fires `change`.
        input.value = '';
        if (!file) return;

        this.clearSelectedFile();
        this.imageChecking = true;
        const objectUrl = URL.createObjectURL(file);
        try {
            const rules = this.limits.image;
            const check = await checkMarketingImage(file, objectUrl, {
                maxBytes: rules.max_bytes,
                minWidth: rules.min_width,
                ratio: { value: parseRatio(rules.ratio), tolerance: rules.ratio_tolerance, label: rules.ratio },
                recommendedWidth: rules.recommended_width,
                recommendedMaxBytes: rules.recommended_max_bytes,
            });
            this.selectedFile = file;
            this.selectedImage = check.info;
            this.selectedImageError = check.error;
            this.selectedImageWarnings = check.warnings;
            if (check.error) {
                URL.revokeObjectURL(objectUrl);
            } else {
                this.selectedObjectUrl = objectUrl;
                this.selectedPreview = this.sanitizer.bypassSecurityTrustUrl(objectUrl);
            }
        } finally {
            this.imageChecking = false;
            this.cdr.detectChanges();
        }
    };

    clearSelectedFile(): void {
        if (this.selectedObjectUrl) {
            URL.revokeObjectURL(this.selectedObjectUrl);
        }
        this.selectedObjectUrl = '';
        this.selectedPreview = null;
        this.selectedFile = null;
        this.selectedImage = null;
        this.selectedImageError = '';
        this.selectedImageWarnings = [];
    }

    submit = async () => {
        if (!this.canSubmit) return;

        try {
            this.saving = true;
            let resp;
            if (this.editorMode === 'create') {
                resp = await this.carouselService.createItem(this.toFormData());
            } else if (this.editorMode === 'edit' && this.editing) {
                resp = await this.carouselService.updateItem(this.editing.id, this.toFields());
            } else if (this.editorMode === 'image' && this.editing && this.selectedFile) {
                const formData = new FormData();
                formData.append('image', this.selectedFile, this.selectedFile.name);
                resp = await this.carouselService.replaceItemImage(this.editing.id, formData);
            } else {
                return;
            }

            if (!resp?.ok) {
                alertifyjs.error(resp?.message || 'No se pudo guardar el banner');
                return;
            }
            alertifyjs.success(`${resp.message || 'Banner guardado'}. Se verá en la app en hasta 5 minutos`);
            this.showWarnings(resp.data?.warnings ?? []);
            this.editorOpen = false;
            this.editing = null;
            this.clearSelectedFile();
            await this.load();
        } catch (error) {
            this.fail(error, 'No se pudo guardar el banner. Intenta de nuevo');
        } finally {
            this.saving = false;
            this.cdr.detectChanges();
        }
    };

    // ── Helpers ────────────────────────────────────────────────────────────────────────────

    private draftFrom(item: CarouselItem): Draft {
        const type = toAppActionType(item.action_type);
        return {
            kind: item.kind ?? 'icon',
            title: item.title ?? '',
            description: item.description ?? '',
            icon: item.icon || 'megaphone',
            ctaLabel: item.cta_label ?? '',
            altText: item.alt_text ?? '',
            actionType: type,
            internalRoute: type === 'internal' ? item.action_target ?? '' : '',
            externalUrl: type === 'external' ? item.action_target ?? '' : '',
            startsOn: toCaracasDay(item.starts_at),
            endsOn: toCaracasDay(item.expires_at),
            status: item.status,
        };
    }

    /** Fields as the backend expects them; the other kind's fields travel empty. */
    private toFields(): CarouselItemFields {
        const draft = this.draft;
        const action = this.draftAction;
        const isIcon = draft.kind === 'icon';
        return {
            title: draft.title.trim(),
            description: isIcon ? draft.description.trim() : '',
            icon: isIcon ? draft.icon : '',
            cta_label: isIcon ? draft.ctaLabel.trim() : '',
            alt_text: isIcon ? '' : draft.altText.trim(),
            action_type: action.type,
            action_target: action.target,
            starts_at: fromCaracasDay(draft.startsOn, false),
            expires_at: fromCaracasDay(draft.endsOn, true),
            status: draft.status,
        };
    }

    private toFormData(): FormData {
        const formData = new FormData();
        formData.append('kind', this.draft.kind);
        for (const [field, value] of Object.entries(this.toFields())) {
            formData.append(field, typeof value === 'boolean' ? String(value) : value);
        }
        if (this.selectedFile) {
            formData.append('image', this.selectedFile, this.selectedFile.name);
        }
        return formData;
    }

    private replaceItem(item: CarouselItem): void {
        this.items = this.items.map(current => (current.id === item.id ? item : current));
        this.activeByKind = { image: 0, icon: 0 };
        for (const current of this.items) {
            if (!current.legacy && current.status && current.kind) this.activeByKind[current.kind] += 1;
        }
    }

    private showWarnings(warnings: string[]): void {
        for (const warning of warnings) {
            alertifyjs.warning(warning);
        }
    }

    private fail(error: unknown, fallback: string): void {
        console.error(fallback, error);
        const message = (error as { error?: { message?: string } })?.error?.message;
        alertifyjs.error(message || fallback);
    }
}
